import React, {CSSProperties, DragEvent, ForwardedRef, forwardRef, lazy, ReactElement, Suspense, SyntheticEvent, useCallback, useEffect, useMemo, useState} from 'react';
import './AssociatedTasks.less';
import TaskComponent from "../TaskComponent";
import * as TaskActions from "@/store/actions/taskActions";
import {connect} from "react-redux";
import util from "@/lib/util";
import * as TaskHelper from "../TaskHelper";
import {handleCheckboxClick, onDuplicate, updateCost} from "../TaskHelper";
import IconButton from "@/ui/IconButton/IconButton";
import Icons from "@/ui/IconButton/Icons";
import TypedIconButton from "@/ui/IconButton/TypedIconButton";
import * as DateUtil from "@/lib/dateutils";
import {isNextUnit, isNextWeek} from "@/lib/dateutils";
import Task from "@/entities/task";
import {confirmError, confirmWarningOkCancel, dialogNoButtons, dialogWithCancel} from "@/ui/Confirm";
import {deleteImpactTask} from "@/store/deps/taskDeps";
import TaskItemChooser from '../TaskItemChooser';
import DivMenu from '@/ui/ContextMenu/DivMenu';
import ImpactUrgency from '@/ui/ImpactUrgency/ImpactUrgency';
import OutlawCalendar from '@/ui/Calendar/OutlawCalendar';
import {formatUserName, getUserInitials} from "@/Contacts/ContactFormat";
import {getCurrentUserId} from "@/lib/picos/currentLogin";
import {useIsMobileView} from "@/hooks/useIsMobileView";
import Loading from "@/ui/Loading";
import find from "@/store/find";
import withErrorBoundaryO from "@/ErrorBoundary/withErrorBoundaryO";
import {startTimer, stopTimer} from "../timers";
import type {RootState} from "@/@types/store";
import {EntityType} from "@/@types/entity";
import {uuid} from "@/@types/schema";
import ArrayMap from "@/lib/ArrayMap";
import Group from "@/entities/group";
import User from "@/entities/user";
import {useRootDispatch} from "@/hooks/useOutlawStore";
import {getZeroUUID} from "@/lib/picos/uuids";
import {EditableTableExportEntry, EditableTableOtherAction, EditableTablePropertyEntry, EditableTableProps, EditableTableSortEntry, Justify, WithItemFn} from "@/ui/EditableTable/EditableTable";
import {existsFilter} from "@/lib/picos/filters";
import {MOBILE_WIDTH} from "@/lib/picos/breakpoints";
import {FilterFn, VoidCallback} from "@/@types/functions";
import {ContextMenuItemProps} from "@/ui/ContextMenu/ContextMenuItem";
import {isToday, isTomorrow} from "date-fns";
import useComponentCache from "@/hooks/useComponentCache";
import {openTaskWindow} from '@/Auth/taskWindow';
import {canReassignTasks} from '../taskTimerState';

const EditableTable = lazy(() => import('@/ui/EditableTable/EditableTable'));

export type AssociatedTasksProps = ReturnType<typeof mapStateToProps> &
    {
        // Should the content be wrapped in a scroller?
        autoScroll?: boolean,

        // The top-level component embedding this widget, used for TaskItem
        topLevel: string,

        // The entityId of the top-level entity
        topLevelId: string | uuid, // can be null if called from Home screen

        // Callback fired right before an edit begins
        onBeforeEdit?: VoidCallback,

        // Called after a new task is added
        onAfterAdd?: VoidCallback,

        // Callback fired right after an edit ends
        onEndEdit?: VoidCallback,

        // Called after a deletion is made
        onAfterDelete?: WithItemFn<Task>,

        // Callback when a task is changed
        onChange?: WithItemFn<Task>,

        // Should we sort by urgency and impact?
        doSort?: boolean,

        // Is this embedded in a modal dialog?
        embedded?: boolean,

        // Environment variables
        env?: any,

        // A filter that will take a Task and return a simple true or false, true if it passes the filter.
        filter?: FilterFn<Task>,

        // A component to show that affects filtering
        filterComponent?: ReactElement,

        // Use EditableTable's flow styles
        flowStyle?: boolean,

        // Should we tell TaskItem to hide the financial summary? Makes sense if we are in here, but TaskGrid table should not.
        hideFinancialSummary?: boolean,

        // Can we do drag and drop?
        isDraggable?: boolean,

        // If draggable, give a drop handler
        onDrop?: Function,

        // Properties to display (in order)
        properties?: EditableTablePropertyEntry<Task>[],

        // Props to handle custom export calls
        exportProps?: EditableTableExportEntry<Task>[],

        // If provided, use this name for file exports
        exportTitle: string,

        // If in mobile view, we will need a callback func for clicking on a contextMenuItem
        onMenuClicked?: (task: Task) => void, // (key, shift, tasks)

        // Actions (name, callback), e.g. { name: 'Add', action: addName }
        // Will always pass back the item that is being acted on as the only parameter to the action
        otherActions?: EditableTableOtherAction<Task>[],

        // Use the tasks given instead of all tasks
        overrideTasks?: Task[],

        // Override the paging size
        pageSize?: number,

        // An object with preset fields for new tasks
        presets?: object,

        // Show the due date
        showDueDate?: boolean,

        // Show impact/urgency
        showPriority?: boolean,

        // Show the client name
        showClientNames?: boolean,

        // Show the completed checkmark
        showCompletionCheck?: boolean,

        // Show the date this task was completed
        showCompletionDate?: boolean,

        // Show the invoice field? On by default
        showInvoice?: boolean,

        // Override showing ANY dates
        showNoDates?: boolean,

        // Show the user (owner)
        showOwner?: boolean,

        // Show the TaskComponent
        showTaskComponent?: boolean,

        // Show the timer widget
        showTimer?: boolean,

        // Show the Completed and Uncompleted toggles?
        showToggles?: boolean,

        // Show the initials for the user that completed the task
        showUserInitials?: boolean,

        // Start with hideCompleted on or off
        hideCompleted?: boolean,

        // Start with hideUncompleted on or off
        hideUncompleted?: boolean,

        // What size do we switch to mobile view (in pixels)?
        mobileWidth?: number,

        // Custom sort function
        sortFn?: (a: Task, b: Task) => number,

        // Add sort functions for custom properties
        sortProps?: EditableTableSortEntry<Task>[],

        // If called from a new, unsaved entity, fill this in
        unsavedEntity?: EntityType,

        // If we are enforcing date ranges, we will give a warning before
        // saving an expense that will fall outside the date range
        enforceDateRange?: boolean,

        // If we are enforcing date ranges, what is the start date?
        dateRangeStart?: Date,

        /** If we are enforcing date ranges, what is the end date? */
        dateRangeEnd?: Date,

        // Allow add/edit/delete
        readOnly?: boolean,

        /* ContentBlockContainer */
        useSmallDisplay?: boolean,

        // If set, deep-link into the task with this ID via EditableTable's startWithId
        startWithId?: string,

        // If set, navigate here when the task editor closes
        returnTo?: string,

        // If set, pass through to TaskItem to deep-link to a specific document
        documentId?: string,

        // If set, update the browser URL for bookmarking
        urlPrefix?: string,
    };

type AssociatedTasksState = {
    // Tasks that are currently being displayed
    startWith: Task | null,
};

/**
 * Most entities within the system have tasks associated with them.  For example:
 * - Categories have tasks related to them. We directly filter for them in TaskGrid.
 * - Tasks have tasks related to them.  We filter for them in LaborCosts.
 *
 * Instead of doing this manually every time, it would be simpler to have a component that
 * can only show tasks that meet certain filtering criteria.
 *
 * It would make sense that we should use TaskGrid's layout engine as well, but that becomes very limiting.
 * In most cases we need to add more attributes to the task, e.g. LaborCosts.
 *
 * As such, we can use a grid view that simulates the color schemes and numbering like the mobile version
 * of TaskView, but still add more columns.
 *
 * Usage:
 *
 * AssociatedTasks is always used within a <ContentBlock>, like so:
 *
 *
 * <ContentBlock key='lead-tasks'  // must be unique for the class you are using it in. Use className-tasks.
 *               title='Tasks'
 *               icon={Icons.TASKS.reactIcon}
 *               customStyles='container--tasks'
 *               onEditCompleted={() => {}}  // Changes are immediate and irrevocable
 *               autoScroll={false}  // We manage scrolling ourselves
 * >
 *    <AssociatedTasks filter={this.taskFilter}  // This is a filter that you create to determine which tasks to show
 *                     presets={{  // This is a list of preset fields for new tasks created here
 *                        relatedTo: current.clients,
 *                        urgency: 'M',
 *                        impact: 'M'
 *                     }}
 *    />
 * </ContentBlock>
 *
 * A filter looks like this:
 *
 * filter = (task) => { Some criteria resulting in a return of a true or false }
 */
const AssociatedTasks = forwardRef(function (props: AssociatedTasksProps, ref: ForwardedRef<HTMLDivElement>) {
    const key = `associated-tasks-${props.topLevel}-${props.topLevelId}`;

    const {cacheValue, cacheSave} = useComponentCache<AssociatedTasksState>(key);
    const dispatch = useRootDispatch();

    // Reactive `isMobile`: re-renders when the window crosses the
    // mobile/desktop boundary so the column-definition branches below
    // produce a fresh `properties` array.  The non-reactive form (called
    // inline in the render body) returns a snapshot that doesn't update
    // on viewport resize without an unrelated state change, which left
    // the EditableTable with the wrong column set after the user moved
    // between mobile and desktop.
    const isMobile = useIsMobileView();

    const altProps = useMemo(() => {
        return {
            autoScroll: true,
            doSort: true,
            hideFinancialSummary: true,
            isDraggable: false,
            showToggles: true,
            showPriority: true,
            showClientNames: true,
            showCompletionDate: true,
            showInvoice: true,
            showOwner: true,
            showTaskComponent: true,
            showTimer: true,
            topLevelId: getZeroUUID(),
            ...props
        }
    }, [props]);

    const [startWith, setStartWith] = useState<Task | null>(cacheValue('startWith', null));

    // OWA-939: unsaved task drafts are no longer reopened automatically. This effect
    // re-ran whenever startWith changed and opened each draft in turn, so two drafts
    // took the editor from each other every second. Drafts are recovered from the
    // Dashboard's "Unsaved drafts" panel.

    // Save the cache
    useEffect(() => {
        return () => {
            cacheSave({startWith})
        }
    }, [cacheSave, startWith]);

    /**
     * Callback from EditableTable after an item has been created.
     *
     * @param {Task} task
     * @param {function} cb - Callback when finished
     * @return {boolean} True if successful, false if canceled
     */
    const onAdd = useCallback((task: Task, cb: Function) => {
        const _doWork = () => {
            const taskClone = updateCost(task);
            taskClone.addedBy = getCurrentUserId();
            taskClone.addedOn = util.newHRTimestamp();
            taskClone.status = 'C';
            if (altProps.topLevel) taskClone.entityType = altProps.topLevel;
            else taskClone.entityType = 'task';
            if (altProps.topLevelId) taskClone.entityId = altProps.topLevelId;
            else taskClone.entityId = taskClone.id;

            dispatch(TaskActions.saveTaskQueue(taskClone));

            if (altProps.onChange) altProps.onChange(taskClone);

            cb();
        };

        if (altProps.enforceDateRange) {
            const lowerBound = altProps.dateRangeStart ? altProps.dateRangeStart < task.completionDate : true;
            const upperBound = altProps.dateRangeEnd >= task.completionDate;
            if (!lowerBound || !upperBound) {
                confirmWarningOkCancel('Warning',
                    `This task's completion date is not within the date range for this invoice.
                    If saved, this task will not be included in this invoice.`,
                    'Save', 'Cancel',
                    () => _doWork(),
                    () => cb(true)
                );
            }
            else _doWork();
        }
        else _doWork();
    }, [altProps, dispatch]);

    /**
     * Callback from EditableTable when an item is edited.
     *
     * @param {Task} task
     * @param cb - Callback when finished
     */
    const onEdit = useCallback((task: Task, cb?: Function) => {
        const _doWork = () => {
            const taskClone = updateCost(task);
            taskClone.addedBy = getCurrentUserId();
            taskClone.addedOn = util.newHRTimestamp();
            taskClone.status = 'U';

            dispatch(TaskActions.saveTaskQueue(taskClone));

            if (altProps.onChange) altProps.onChange(taskClone);

            cb();
        };

        if (altProps.enforceDateRange) {
            const lowerBound = altProps.dateRangeStart ? altProps.dateRangeStart < task.completionDate : true;
            const upperBound = altProps.dateRangeEnd >= task.completionDate;
            if (!lowerBound || !upperBound) {
                confirmWarningOkCancel('Warning',
                    `This task's completion date is not within the date range for this invoice.
                    If saved, this task will not be included in this invoice.`,
                    'Save', 'Cancel',
                    () => _doWork(),
                    () => cb(true)
                );
            }
            else _doWork();
        }
        else _doWork();
    }, [altProps, dispatch]);

    /**
     * Can this task be deleted?
     * 1. If it is associated with an active invoice, then no.
     * 2. If there is an impact that stops a delete, then no.
     */
    const canDelete = useCallback((task: Task) => {
        if (task.invoiceId) {
            const invoice = altProps.invoices.get(task.invoiceId);
            return !invoice || invoice.status !== 'D';
        }

        const impact = util.getDeleteImpact('task', task, deleteImpactTask);
        return (impact.canDelete);
    }, [altProps.invoices]);

    /**
     * If the task is not deletable, tell the user why.
     */
    const canDeleteMessage = useCallback((task: Task) => {
        if (task.invoiceId) {
            const invoice = altProps.invoices.get(task.invoiceId);
            if (!invoice || invoice.status !== 'D') return '';
            else return `Associated with invoice ${invoice.invoiceNumber} [${invoice.id}]`;
        }

        const impact = util.getDeleteImpact('task', task, deleteImpactTask);
        if (impact.canDelete) return '';
        else return impact.message;
    }, [altProps.invoices]);

    /**
     * Callback from EditableTable when an item has been deleted.
     *
     * @param {Task} task
     * @param cb - Callback when finished
     * @param {MouseEvent} e
     */
    const onDelete = useCallback((task: Task, cb: Function) => {
        TaskHelper.onDelete(null, task);

        altProps.onChange?.(task);

        if (typeof (cb) === 'function') cb();
    }, [altProps]);

    const tasks: Task[] = useMemo(() => {
        let _tasks: Task[] | ArrayMap<Task>;
        if (altProps.overrideTasks) _tasks = altProps.overrideTasks;
        else {
            switch (altProps.topLevel) {
                case 'matter':
                    _tasks = altProps.tasks.getMappingFor('matterId', altProps.topLevelId);
                    break;
                case 'lead':
                    _tasks = altProps.tasks.getMappingFor('leadId', altProps.topLevelId);
                    break;
                case 'campaign':
                    _tasks = altProps.tasks.getMappingFor('campaignId', altProps.topLevelId);
                    break;
                case 'contact':
                    _tasks = altProps.tasks.getMappingFor('contactId', altProps.topLevelId);
                    break;
                default:
                    _tasks = altProps.tasks;
                    break;
            }
        }

        _tasks = _tasks.filter(util.combineFilters([
            altProps.filter,
            (t: Task) => t.impact && t.urgency && t.title,
            util.existsFilter
        ].filter(util.existsFilter)));

        // Now we need to sort based on urgency and impact (if required)
        const defaultSortFn = (a: Task, b: Task): number => {
            const aIU = TaskHelper.getGridNumber(a) ?? 9;
            const bIU = TaskHelper.getGridNumber(b) ?? 9;

            if (aIU !== bIU) return aIU - bIU;
            else return ((b.dueDate?.getTime()) || 0) - (a.dueDate?.getTime() || 0);
        };

        if (altProps.doSort) return _tasks.sort(altProps.sortFn || defaultSortFn);
        else return _tasks;
    }, [altProps.overrideTasks, altProps.filter, altProps.doSort, altProps.sortFn, altProps.topLevel, altProps.tasks, altProps.topLevelId]);

    /**
     * Toggle between hiding and showing completed items.
     *
     * @param {Task} task
     * @param {boolean} enabled
     * @return {boolean}
     */
    const hideCompleted = useCallback((task: Task, enabled: boolean): boolean => {
        if (enabled) {
            return task.completionDate === null;
        }
        else return true;
    }, []);

    /**
     * Toggle between hiding and showing uncompleted tasks.
     *
     * @param {Task} task
     * @param {boolean} enabled
     * @return {boolean}
     */
    const hideUncompleted = useCallback((task: Task, enabled: boolean) => {
        if (enabled) {
            return task.completionDate !== null;
        }
        else return true;
    }, []);

    const onMoveToday = useCallback((e: SyntheticEvent, task: Task) => {
        TaskHelper.onMoveDays([task], DateUtil.today(task.dueDate), e);
    }, []);

    const onMoveTomorrow = useCallback((e: SyntheticEvent, task: Task) => {
        TaskHelper.onMoveDays([task], DateUtil.tomorrow(new Date()), e);
    }, []);

    const onMoveNextWeek = useCallback((e: SyntheticEvent, task: Task) => {
        TaskHelper.onMoveDays([task], DateUtil.nextWeek(task.dueDate), e);
    }, []);

    const onMoveNextMonth = useCallback((e: SyntheticEvent, task: Task) => {
        TaskHelper.onMoveDays([task], DateUtil.nextMonth(task.dueDate), e);
    }, []);

    const onMoveCustom = useCallback((e: SyntheticEvent, task: Task) => {
        const onChange = (date: Date, onClose: () => void) => {
            TaskHelper.onMoveDays([task], date, e);

            onClose();
        }

        dialogWithCancel('Pick a Date',
            <OutlawCalendar
                onChange={onChange}
                defaultValue={new Date()}
                isRange={false}
                dateFormat='dd'
                hideToday={true}
            />, {maxWidth: '30em'}
        );
    }, []);

    const onMarkPrivate = useCallback((task: Task) => {
        TaskHelper.onChangePrivacy([task], !task.private);
    }, []);

    // DEPRECATED: Use TaskHelper.tsx::onDuplicate!
    // const onDuplicate = useCallback((e: MouseEvent, task: Task) => {
    //     e.preventDefault();
    //
    //     const taskClone = task.duplicate('title');
    //     dispatch(TaskActions.saveTaskQueue(taskClone));
    // }, [dispatch]);

    /**
     *
     * @param {Task} task
     * @param {User} user
     */
    const onReassignTask = useCallback((task: Task, user: User) => {
        if (!canReassignTasks(task)) return;

        const taskClone = task.clone();

        taskClone.assignedTo = user.id;
        taskClone.addedBy = getCurrentUserId();
        taskClone.addedOn = util.newHRTimestamp();

        dispatch(TaskActions.saveTaskQueue(taskClone));
    }, [dispatch]);

    /**
     * Get actions for a task.
     *
     * @param {Task} task
     */
    const getContextMenuItems = useCallback((task: Task) => {
        const today = {
            label: 'Today',
            icon: Icons.CIRCLEDOT,
            color: 'darkorange',
            onClick: util.bouncify((e: SyntheticEvent) => onMoveToday(e, task))
        }

        const tomorrow = {
            label: 'Next Day',
            icon: Icons.CIRCLERIGHT,
            color: 'darkorange',
            onClick: util.bouncify((e: SyntheticEvent) => onMoveTomorrow(e, task))
        };

        const nextWeek = {
            label: 'Next week',
            icon: Icons.CIRCLERIGHT,
            color: 'darkorange',
            onClick: (e: SyntheticEvent) => onMoveNextWeek(e, task)
        };

        const nextMonth = {
            label: 'Next month',
            icon: Icons.CIRCLERIGHT,
            color: 'darkorange',
            onClick: (e: SyntheticEvent) => onMoveNextMonth(e, task)
        };

        const customDate = {
            label: 'Pick a Date',
            icon: Icons.CALENDAR,
            color: 'darkorange',
            onClick: (e: SyntheticEvent) => onMoveCustom(e, task)
        };

        const moveTargets = [];
        if (!isToday(task.dueDate)) moveTargets.push(today);
        if (!isTomorrow(task.dueDate)) moveTargets.push(tomorrow);
        if (!isNextWeek(task.dueDate)) moveTargets.push(nextWeek);
        if (!isNextUnit(task.dueDate, 'M')) moveTargets.push(nextMonth);
        moveTargets.push(customDate);

        const moveTo = {
            label: 'Move to', icon: Icons.CIRCLERIGHT, color: 'darkorange', onClick: util.noop,
            subItems: moveTargets
        };

        const assignTo: ContextMenuItemProps = {
            label: 'Assign to',
            icon: Icons.USER,
            color: '#cc9966',
            onClick: util.noop,
            subItems: altProps.users.filter(u => u.enabled && u.status !== 'D')
                .filter(u => {
                    // If assignedTo is null, show everybody except the current user
                    if (task.assignedTo === null) return getCurrentUserId() !== u.id;
                    // If assignedTo is set, allow anybody except the assignedTo user
                    else return u.id !== task.assignedTo;
                })
                .map(u => {
                    return {
                        label: formatUserName(u, {shorten: true, format: 'FML'}),
                        icon: Icons.USER,
                        color: '#cc9966',
                        onClick: util.bouncify(() => onReassignTask(task, u))
                    };
                })
        };

        const items: ContextMenuItemProps[] = [
            {
                label: `Mark ${task.private ? 'public' : 'private'}`,
                icon: Icons.PRIVATE,
                color: task.private ? 'black' : 'rgba(255,0,0,.6)',
                onClick: util.bouncify(() => onMarkPrivate(task)),
            },
            {
                label: 'Duplicate',
                icon: Icons.CLONE,
                color: 'navy',
                onClick: () => onDuplicate([task]),
            },
            moveTo
        ].filter(util.existsFilter) as ContextMenuItemProps[];

        if (altProps.users.length > 1 && canReassignTasks(task)) items.push(assignTo);

        if (!task.invoiceId) items.push({
            label: 'Delete',
            icon: Icons.DELETE,
            showIf: canDelete,
            onClick: util.bouncify((e: SyntheticEvent) => {
                e.preventDefault();

                onDelete(task, util.noop)
            })
        });

        return items;
    }, [canDelete, onDelete, onMarkPrivate, onMoveCustom, onMoveNextMonth, onMoveNextWeek, onMoveToday, onMoveTomorrow, onReassignTask, altProps.users]);

    const popupTask = useCallback((task: Task | Task[]) => {
        const tasks = Array.isArray(task) ? task : [task];
        const results = tasks.map(t => openTaskWindow(t.id));
        const blocked = results.filter(result => result === 'blocked').length;
        const unsafe = results.filter(result => result === 'unsafe').length;
        if (blocked || unsafe) confirmError('Task window not opened', [
            blocked ? `${blocked} task window${blocked === 1 ? ' was' : 's were'} blocked. Allow pop-ups for this site and try again.` : '',
            unsafe ? `${unsafe} task window${unsafe === 1 ? ' was' : 's were'} stopped because its copied sign-in state could not be made safe. Your current tab remains signed in.` : '',
        ].filter(Boolean).join(' '));
    }, []);

    const showContextMenu = useCallback((task: Task | Task[]) => {
        const fn = () => {
            if (altProps.onMenuClicked) setTimeout((task: Task) => altProps.onMenuClicked(task as Task), 100);
        };

        dialogNoButtons('Task Actions',
            <DivMenu onSelection={fn}
                     items={getContextMenuItems(task as Task)}
            />,
            {}
        )
    }, [getContextMenuItems, altProps]);

    const onAfterAdd = useCallback(() => {
        setStartWith(null);

        if (altProps.onAfterAdd) altProps.onAfterAdd();
        else if (altProps.onEndEdit) altProps.onEndEdit();
    }, [altProps, setStartWith]);

    const onEndEdit = useCallback(() => {
        setStartWith(null);

        if (altProps.onEndEdit) altProps.onEndEdit();
    }, [altProps, setStartWith]);

    const onBeforeEdit = useCallback(() => {
        if (altProps.onBeforeEdit) altProps.onBeforeEdit();
    }, [altProps]);

    const renderTask = useCallback((task: Task) => {
        let elapsedTime = task.running
                          ? task.lastStart
                            ? new Date().getTime() - task.lastStart
                            : 0
                          : 0;

        return <TaskComponent
            key={`${task.id}T${elapsedTime}`}
            clickPassThrough={true}
            contextMenuItems={getContextMenuItems(task)}
            editTitle={false}
            expandCell={null}
            isSelected={false}
            onBeforeEdit={altProps.onBeforeEdit}
            onEndEdit={altProps.onEndEdit}
            onCheckboxClick={handleCheckboxClick}
            showRelatedTo={true}
            showClientNames={altProps.showClientNames}
            showDates={altProps.showNoDates ? false : altProps.showDueDate || !altProps.showCompletionDate}
            showOwner={altProps.showOwner}
            task={task}
            unsavedEntity={altProps.unsavedEntity}
            useOneLine={true}
            hideActionsOnMobile={true}
        />;
    }, [getContextMenuItems, altProps.onBeforeEdit, altProps.onEndEdit, altProps.showClientNames, altProps.showNoDates, altProps.showDueDate, altProps.showCompletionDate, altProps.showOwner, altProps.unsavedEntity]);

    /**
     * On-click handler.
     *
     * @param {Task} task
     */
    const handleCheck = useCallback((task: Task) => {
        handleCheckboxClick(task);
    }, []);

    const onDrop = useCallback((e: DragEvent, source: Task, target: Task) => {
        if (altProps.onDrop) {
            altProps.onDrop(e, source, target);
        }
    }, [altProps]);

    /**
     * Tell EditableTable what background styles to apply to this row.
     * By default, we only apply styles to uncompleted tasks, so that the focus is on tasks that need attention.
     *
     * @param {Task} task
     * @returns {{}|{backgroundImage: string}}
     */
    const rowStyleFn = useCallback((task: Task) => {
        // Do not show color-coded backgrounds if using flowStyle (invoices).
        if (altProps.flowStyle) return {};

        // Otherwise get the background based on the impact and priority.
        return {backgroundColor: TaskHelper.getTaskBackgroundColor(task)};
    }, [altProps.flowStyle]);

    const renderIcons = useCallback((task: Task) => {
        const checkbox = task.completionDate
                         ? <TypedIconButton type={Icons.CHECKED}
                                            onClick={handleCheck}
                                            data={task}
                         />
                         : <IconButton type={Icons.UNCHECKED}
                                       onClick={handleCheck}
                                       data={task}
                         />;

        const playIcon = TaskHelper.canPlay(task)
                         ? <IconButton
                             id={`PLAY__${task.title}`}
                             type={task.running ? Icons.PAUSE : Icons.PLAY}
                             size={Icons.IconSizeNormal}
                             style={{marginLeft: '0px'}}
                             color={task.running ? 'red' : 'green'}
                             title={task.running ? 'Pause Timer' : 'Start Timer'}
                             onClick={task.running ? () => stopTimer(task) : () => startTimer(task)}
                         /> : null;

        return <div className='u-column'>
            {checkbox}
            {playIcon}
        </div>
    }, [handleCheck]);

    const renderUserInitials = useCallback((task: Task) => {
        if (task.assignedToType === 'G') {
            const group = find<Group>('groups', task.assignedTo);
            if (group) return group.name;
        } else {
            const user = find<User>('users', task.assignedTo);
            if (user) return getUserInitials(task.assignedTo, 3);
        }
        return '???';
    }, []);

    const renderCompletionDate = useCallback((task: Task) => {
        if (task.completionDate) {
            return DateUtil.formatDate(task.completionDate, false, false, false);
        }
        else return null;
    }, []);

    const renderTimer = useCallback((task: Task) => {
        if (TaskHelper.canPlay(task)) {
            return <IconButton
                id={`PLAY__${task.title}`}
                type={task.running ? Icons.PAUSE : Icons.PLAY}
                size={Icons.IconSizeNormal}
                style={{marginLeft: '0px'}}
                color={task.running ? 'red' : 'green'}
                title={task.running ? 'Pause Timer' : 'Start Timer'}
                onClick={task.running ? () => stopTimer(task) : () => startTimer(task)}
            />;
        }
    }, []);

    const sortByDate = useCallback((a: Task, b: Task) => {
        return (a.completionDate?.getTime() ?? 0) - (b.completionDate?.getTime() ?? 0);
    }, []);

    const extraTopMargin = {marginTop: '3px'};
    let properties: EditableTablePropertyEntry<Task>[] = [];
    let sortProps = altProps.sortProps || [];
    let exportProps: EditableTableExportEntry<Task>[] = altProps.exportProps || [];
    if (altProps.sortProps) sortProps = sortProps.concat(...altProps.sortProps);

    if (altProps.showPriority) {
        properties.push({
            label: 'impact',
            displayLabel: 'I/U',
            justify: Justify.CENTER,
            width: 'max-content',
            showAtWidth: 900,
            renderItemFn: (task: Task) => <div><ImpactUrgency task={task} mode='capsule' useNumbers={false}/></div>
        });

        sortProps.push({
            label: 'impact',
            type: 'number',
            sortFn: (a: Task, b: Task) => (TaskHelper.getGridNumber(b) ?? 9) - (TaskHelper.getGridNumber(a) ?? 9)
        });

        exportProps.push({
            label: 'impact',
            displayLabel: 'Impact/Urgency',
            type: 'string',
            exportFn: (t: Task) => `${t.impact}/${t.urgency}`
        });
    }

    if (isMobile && (altProps.showCompletionCheck || altProps.showTimer)) {
        properties.push({
            label: 'icons',
            displayLabel: <IconButton type={Icons.CHECKEDSOLID} color='green' style={extraTopMargin}/>,
            width: '1.6em',
            noClick: true,
            renderItemFn: renderIcons
        });

        exportProps.push({label: 'icons', doNotExport: true});
    }

    if (!isMobile && (altProps.showCompletionCheck || altProps.showTimer)) {
        properties.push({
            label: 'completionDateIcon',
            displayLabel: <IconButton type={Icons.CHECKED} style={extraTopMargin}/>,
            justify: Justify.CENTER,
            width: '1.6em',
            noClick: true,
            renderItemFn: (task: Task) => task.completionDate
                                          ? <TypedIconButton type={Icons.CHECKED}
                                                             onClick={handleCheck}
                                                             data={task}
                                                             style={extraTopMargin}
                                          />
                                          : <TypedIconButton type={Icons.UNCHECKED}
                                                             onClick={handleCheck}
                                                             data={task}
                                          />
        });

        exportProps.push({
            label: 'completionDateIcon',
            displayLabel: 'Done',
            type: 'string',
            exportFn: (t) => t.completionDate ? '✓' : ''
        });
    }

    if (altProps.showInvoice && !isMobile) {
        const getInvoiceNumber = (task: Task) => {
            return task.invoiceId ? altProps.invoices.get(task.invoiceId)?.invoiceNumber : '';
        };

        properties.push({
            label: 'invoice',
            renderItemFn: getInvoiceNumber,
            noClick: true,
            width: 'max-content'
        });

        sortProps.push({
            label: 'invoice',
            type: 'string',
            useFormatted: true
        });

        exportProps.push({
            label: 'invoice',
            type: 'string',
            exportFn: getInvoiceNumber
        });
    }

    if (altProps.showUserInitials && !isMobile) {
        properties.push({
            label: 'User', renderItemFn: renderUserInitials,
            width: 'max-content', showAtWidth: 900, type: 'string'
        });

        sortProps.push({label: 'User', type: 'string', useFormatted: true});

        exportProps.push({label: 'User', displayLabel: 'User', exportFn: renderUserInitials});
    }


    if (!isMobile && altProps.showTimer) {
        properties.push({
            label: 'timer',
            justify: Justify.CENTER,
            displayLabel: <IconButton type={Icons.CLOCK} style={extraTopMargin}/>,
            renderItemFn: renderTimer,
            noClick: true,
            width: '1.6em'
        });

        exportProps.push({label: 'timer', displayLabel: 'Timer', doNotExport: true});
    }

    if (altProps.showCompletionDate) {
        properties.push({
            label: 'completionDate',
            displayLabel: 'Done',
            width: 'max-content',
            hideOnMobile: false,
            renderItemFn: renderCompletionDate
        });

        sortProps.push({label: 'completionDate', type: 'date', order: 'desc'});

        exportProps.push({label: 'completionDate', displayLabel: 'Done', type: 'string'});
    }

    if (altProps.showTaskComponent) {
        properties.push({
            label: 'task',

            renderItemFn: renderTask,
            width: '1fr'
        });

        sortProps.push({
            label: 'task',
            type: 'date',
            sortFn: sortByDate
        });

        exportProps.push({
            label: 'task',
            displayLabel: 'Title',
            type: 'string',
            exportFn: t => t.title
        })
    }

    if (altProps.properties) {
        properties = properties.concat(altProps.properties);

        exportProps = [...altProps.properties
            .map(p => {
                const ep = exportProps.find(ep => ep.label === p.label);

                if (!ep || ep.doNotExport) return null;

                const entry: EditableTableExportEntry<Task> = {
                    label: p.label,
                    displayLabel: ep?.displayLabel
                                  ? typeof (ep.displayLabel) === 'string'
                                    ? ep.displayLabel
                                    : ep.label
                                  : p.label,
                    exportFn: ep?.exportFn ?? p.renderItemFn as (item: any) => string,
                    type: ep?.type ?? 'string',
                    useFormatted: !!ep && ep.useFormatted != null
                                  ? ep.useFormatted
                                  : !!(ep?.exportFn)
                                    ? false
                                    : !!p.renderItemFn
                };

                return entry;
            }).filter(existsFilter),
            ...exportProps.filter(ep => !altProps.properties.find(p => p.label === ep.label))]
    }

    const otherActions = altProps.otherActions || [];
    if (isMobile) {
        otherActions.push({
            label: 'ellipses',
            action: showContextMenu,
            selectedIcon: Icons.ELLIPSES
        });
    }

    otherActions.push({
        label: 'popup',
        displayLabel: 'Open task in a new window',
        action: popupTask,
        selectedIcon: Icons.POPUP_WINDOW,
        color: 'navy'
    })

    // Never use more than 100 entries per screen, because it gets SLOW
    const pageSize = altProps.pageSize || Math.max(100, Math.round(tasks.length / 20));

    const style: CSSProperties = {};
    if (altProps.autoScroll) style.overflowY = 'unset';

    const TasksTable = EditableTable as unknown as React.ComponentType<Partial<EditableTableProps<Task>>>;

    const toggles = useMemo(() => altProps.showToggles ? [
        {
            label: 'Hide Completed',
            toggleFilterFn: hideCompleted,
            enabled: altProps.hideCompleted
        },
        {
            label: 'Hide Uncompleted',
            toggleFilterFn: hideUncompleted,
            enabled: altProps.hideUncompleted
        }
    ] : undefined, [hideCompleted, hideUncompleted, altProps.hideCompleted, altProps.hideUncompleted, altProps.showToggles]);

    const env = useMemo(() => ({
        ...altProps.env,
        topLevel: altProps.topLevel,
        topLevelId: altProps.topLevelId,
        unsavedEntity: altProps.unsavedEntity
    }), [altProps.env, altProps.topLevel, altProps.topLevelId, altProps.unsavedEntity]);

    const editorProps = useMemo(() => ({
        inAssociatedTasks: true,
        hideFinancialSummary: altProps.hideFinancialSummary,
        useSmallDisplay: altProps.useSmallDisplay,
        param: 'task',
        keyName: `${altProps.topLevel}_${altProps.topLevelId}`,
        documentId: altProps.documentId,
    }), [altProps.hideFinancialSummary, altProps.topLevel, altProps.topLevelId, altProps.useSmallDisplay, altProps.documentId]);

    const customAddLabels = useMemo(() => [
        {label: 'Add', icon: Icons.ADD, param: 'task'},
        {label: 'Template', icon: Icons.CLONE, param: 'template'}
    ], []);

    return (
        <div className='associated-tasks' style={style} ref={ref}>
            <Suspense fallback={<Loading/>}>
                <TasksTable
                    name={key}
                    entityName='task'
                    entityId={altProps.topLevelId}
                    hideGridOnEdit={true}
                    hideStandardActions={isMobile}
                    customAddLabels={customAddLabels}
                    editor={TaskItemChooser}
                    editorProps={editorProps}
                    embedded={altProps.embedded}
                    env={env}
                    mobileWidth={altProps.mobileWidth || MOBILE_WIDTH}
                    rowStyleFn={rowStyleFn}
                    flowStyle={altProps.flowStyle}
                    onAdd={altProps.readOnly ? null : onAdd}
                    onEdit={onEdit}
                    onBeforeAdd={onBeforeEdit}
                    onBeginEdit={onBeforeEdit}
                    onAfterAdd={onAfterAdd}
                    onEndEdit={onEndEdit}
                    onAfterDelete={altProps.onAfterDelete}
                    pageSize={pageSize}
                    canAdd={!isMobile}
                    canDeleteFn={isMobile ? () => false : canDelete}
                    canDeleteMessageFn={canDeleteMessage}
                    onDelete={altProps.readOnly ? null : onDelete}
                    uniqueKey='title'
                    items={tasks}
                    properties={properties}
                    sortProps={sortProps}
                    exportProps={exportProps}
                    exportTitle={altProps.exportTitle}
                    otherActions={otherActions}
                    presets={altProps.presets}
                    isDraggable={altProps.isDraggable}
                    onDrop={onDrop}
                    onDragEnd={TaskHelper.onDragEnd}
                    onDragStart={TaskHelper.onDragStart}
                    startWith={startWith}
                    startWithId={altProps.startWithId}
                    returnTo={altProps.returnTo}
                    urlPrefix={altProps.urlPrefix}
                    toggles={toggles}
                    filterComponent={altProps.filterComponent}
                />
            </Suspense>
        </div>
    )
});

AssociatedTasks.displayName = 'AssociatedTasks';

const mapStateToProps = (state: RootState) => {
    return {
        contacts: state.contacts,
        invoices: state.invoices,
        tasks: state.tasks,
        users: state.users,
        restoreStatus: state.misc.restoreStatus
    };
};


export default withErrorBoundaryO(connect(mapStateToProps, null, null, {forwardRef: true})(AssociatedTasks), {name: 'Associated Tasks', shouldUseErrorFrame: true});
